// BUILD=R23-login-redirect-hardening
import { useState, useEffect, useRef } from "react";
import { User } from "./types";
import { validateHybridSession } from "./hybridAuthOperations";
import { ensureHybridSession } from "./ensureHybridSession";

/**
 * Grace-period: als de session-token korter dan dit geleden is aangemaakt
 * (expires_at minus 8u), nemen we transient validatiefouten NIET als reden
 * om de gebruiker uit te loggen. Voorkomt dat een net-aangemaakte sessie
 * direct na login wordt weggegooid door RLS-replicatievertraging of een
 * netwerk-glitch.
 */
const POST_LOGIN_GRACE_MS = 15_000;

const isWithinGracePeriod = (): boolean => {
  const expiresAt = localStorage.getItem("auth_session_expires");
  if (!expiresAt) return false;
  // Sessie is 24u geldig → createdAt ≈ expires - 24h
  const expires = new Date(expiresAt).getTime();
  const approxCreatedAt = expires - 24 * 60 * 60 * 1000;
  return Date.now() - approxCreatedAt < POST_LOGIN_GRACE_MS;
};

export const useAuthState = () => {
  const [isSessionValid, setIsSessionValid] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(true);

  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem("currentUser");
    if (!savedUser) return null;
    try {
      const parsed = JSON.parse(savedUser);
      if (parsed && !parsed.company) parsed.company = "luijten_vvz";
      return parsed;
    } catch {
      return null;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem("isAuthenticated") === "true";
  });

  const [selectedPackers, setSelectedPackers] = useState<string[]>(() => {
    const savedPackers = localStorage.getItem("selectedPackers");
    return savedPackers ? JSON.parse(savedPackers) : [];
  });

  // Mount-only validate-effect. Re-running bij elke isAuthenticated/user-change
  // veroorzaakte een race direct na login. Periodieke check via interval.
  const userIdRef = useRef<string | null>(user?.id ?? null);
  const isAuthRef = useRef<boolean>(isAuthenticated);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
    isAuthRef.current = isAuthenticated;
  }, [user?.id, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    const forceLogout = (reason: string) => {
      console.log(`[useAuthState] forceLogout: ${reason}`);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("currentUser");
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("selectedPackers");
    };

    const validateSession = async () => {
      if (cancelled) return;
      setIsValidating(true);

      const sessionToken = localStorage.getItem("auth_session_token");
      const expiresAt = localStorage.getItem("auth_session_expires");
      const currentUserId = userIdRef.current;
      const currentIsAuth = isAuthRef.current;

      console.log("[login-flow] validateSession entry", {
        hasToken: !!sessionToken,
        hasExpiry: !!expiresAt,
        userId: currentUserId,
        isAuth: currentIsAuth,
      });

      // Geen token aanwezig
      if (!sessionToken || !expiresAt) {
        if (currentIsAuth && currentUserId) {
          console.log("[useAuthState] Auto-restoring missing session");
          const result = await ensureHybridSession(currentUserId);
          if (cancelled) return;
          if (result.success) {
            setIsSessionValid(true);
            window.dispatchEvent(new Event("session-restored"));
            setIsValidating(false);
            return;
          }
          // Alleen force-logout bij definitieve reden
          if (result.error === "Account niet actief of niet geregistreerd") {
            forceLogout(result.error);
          } else {
            console.warn(
              "[useAuthState] ensureHybridSession transient fail; behoud lokale state:",
              result.error,
            );
          }
        }
        setIsSessionValid(false);
        setIsValidating(false);
        return;
      }

      // Client-side expiry check
      if (new Date() > new Date(expiresAt)) {
        console.log("[useAuthState] Session expired client-side");
        localStorage.removeItem("auth_session_token");
        localStorage.removeItem("auth_session_expires");
        if (currentIsAuth && currentUserId) {
          const result = await ensureHybridSession(currentUserId);
          if (cancelled) return;
          if (result.success) {
            setIsSessionValid(true);
            window.dispatchEvent(new Event("session-restored"));
            setIsValidating(false);
            return;
          }
          if (result.error === "Account niet actief of niet geregistreerd") {
            forceLogout(result.error);
          }
        }
        setIsSessionValid(false);
        setIsValidating(false);
        return;
      }

      // Server-side validate
      try {
        const isValid = await validateHybridSession();
        if (cancelled) return;
        console.log("[useAuthState] Server validation result:", isValid);

        if (isValid) {
          setIsSessionValid(true);
          setIsValidating(false);
          return;
        }

        // Niet valide volgens server. Grace-period? → behoud, geen logout.
        if (isWithinGracePeriod()) {
          console.warn(
            "[useAuthState] Server validation faalde binnen grace-period; behoud lokale state",
          );
          setIsSessionValid(true);
          setIsValidating(false);
          return;
        }

        // Buiten grace: probeer te restoren, alleen logout bij definitieve reden
        localStorage.removeItem("auth_session_token");
        localStorage.removeItem("auth_session_expires");
        if (currentIsAuth && currentUserId) {
          const result = await ensureHybridSession(currentUserId);
          if (cancelled) return;
          if (result.success) {
            setIsSessionValid(true);
            window.dispatchEvent(new Event("session-restored"));
          } else if (result.error === "Account niet actief of niet geregistreerd") {
            forceLogout(result.error);
          } else {
            console.warn(
              "[useAuthState] Restore transient fail; behoud lokale state",
            );
          }
        }
        setIsValidating(false);
      } catch (error) {
        console.error("[useAuthState] Validation error (transient):", error);
        // Transient fout → niet uitloggen
        if (!cancelled) {
          setIsSessionValid(false);
          setIsValidating(false);
        }
      }
    };

    validateSession();
    const interval = setInterval(validateSession, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // Mount-only — bewust geen deps, voorkomt race vlak na login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist state to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem("currentUser", JSON.stringify(user));
      localStorage.setItem("isAuthenticated", "true");
    } else {
      localStorage.removeItem("currentUser");
      localStorage.setItem("isAuthenticated", "false");
    }
  }, [user, isAuthenticated]);

  // Update session validity zodra login state set is + token aanwezig.
  useEffect(() => {
    if (user && isAuthenticated) {
      const sessionToken = localStorage.getItem("auth_session_token");
      const expiresAt = localStorage.getItem("auth_session_expires");
      if (sessionToken && expiresAt && new Date() < new Date(expiresAt)) {
        console.log("[login-flow] post-login: marking session valid");
        setIsSessionValid(true);
        setIsValidating(false);
      }
    }
  }, [user, isAuthenticated]);

  return {
    user,
    setUser,
    isAuthenticated,
    setIsAuthenticated,
    selectedPackers,
    setSelectedPackers,
    isSessionValid,
    isValidating,
  };
};
